import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import type {
  CreditLimitDto,
  SubjectRequirementScope,
  UpsertCreditLimitDto,
} from '../../services/licenseComplianceService';
import type { EducationCategory } from '../../services/cpeHistoryService';
import {
  DELIVERY_METHOD_TOKENS,
  formatDeliveryMethods,
  getDeliveryMethodLabel,
} from '../../utils/deliveryMethods';
import { getEducationCategoriesMemo } from '../../utils/creditLimitBasis';

type CreditLimitBasis = 'delivery' | 'field';

interface CreditLimitDialogProps {
  open: boolean;
  /** When set, the dialog edits this limit; otherwise it creates a new one. */
  limit?: CreditLimitDto | null;
  /** Called with the validated payload; the parent performs the API call. */
  onSubmit: (dto: UpsertCreditLimitDto) => Promise<void>;
  onClose: () => void;
}

/**
 * Add/edit dialog for a credit limit: caps either delivery methods or fields
 * of study (one basis per limit), with max hours and/or max percent, scope,
 * and an optional display name. Hours beyond a cap never count toward the
 * total requirement — a limit is a ceiling, not a goal.
 */
export default function CreditLimitDialog({ open, limit, onSubmit, onClose }: CreditLimitDialogProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [basis, setBasis] = useState<CreditLimitBasis>('delivery');
  const [methods, setMethods] = useState<string[]>([]);
  const [categories, setCategories] = useState<EducationCategory[]>([]);
  const [categoryOptions, setCategoryOptions] = useState<EducationCategory[] | null>(null);
  const [categoryLoadFailed, setCategoryLoadFailed] = useState(false);
  const [maxHours, setMaxHours] = useState<string>('');
  const [maxPercent, setMaxPercent] = useState<string>('');
  const [scope, setScope] = useState<SubjectRequirementScope>('total_period');
  const [name, setName] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Reset form state each time the dialog opens
  useEffect(() => {
    if (!open) return;
    const isFieldBasis = (limit?.educationCategoryIds.length ?? 0) > 0;
    setBasis(isFieldBasis ? 'field' : 'delivery');
    setMethods(limit?.deliveryMethods ?? []);
    // Seed selected categories from the limit's ids + server-resolved names so
    // chips render before (or without) the options list loading.
    setCategories(
      isFieldBasis && limit
        ? limit.educationCategoryIds.map((id, i) => ({
            id,
            name: limit.educationCategoryNames[i] ?? `Category ${id}`,
          }))
        : []
    );
    setCategoryLoadFailed(false);
    setMaxHours(limit?.maxHours != null ? String(limit.maxHours) : '');
    setMaxPercent(limit?.maxPercent != null ? String(limit.maxPercent) : '');
    setScope(limit?.scope ?? 'total_period');
    setName(limit?.name ?? '');
    setSubmitError(null);
    setSaving(false);
  }, [open, limit]);

  // Lazy-load the category list the first time the field basis is active.
  // Global seed data, memoized across dialog opens; failures retry next time.
  useEffect(() => {
    if (!open || basis !== 'field' || categoryOptions !== null) return;
    let cancelled = false;
    setCategoryLoadFailed(false);
    getEducationCategoriesMemo()
      .then((list) => {
        if (!cancelled) setCategoryOptions(list);
      })
      .catch(() => {
        if (!cancelled) setCategoryLoadFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [open, basis, categoryOptions]);

  const maxHoursNum = Number(maxHours);
  const maxPercentNum = Number(maxPercent);

  const activeSelectionCount = basis === 'delivery' ? methods.length : categories.length;

  const suggestedName = useMemo(() => {
    if (basis === 'delivery') {
      return methods.length > 0 ? `${formatDeliveryMethods(methods)} limit` : null;
    }
    return categories.length > 0 ? `${categories.map((c) => c.name).join(', ')} limit` : null;
  }, [basis, methods, categories]);

  // Field-level errors only flag invalid input; an incomplete form just keeps
  // the submit button disabled without shouting at the user mid-task.
  const hoursError = useMemo(() => {
    if (!maxHours) return null;
    if (!Number.isFinite(maxHoursNum) || maxHoursNum <= 0) {
      return 'Must be greater than 0.';
    }
    // Credits increment in tenths, so caps do too. String check on the raw
    // input — float math (e.g. n * 10) false-rejects values like 20.3.
    if (!/^(\d+|\d*\.\d)$/.test(maxHours.trim())) {
      return 'Use at most one decimal place.';
    }
    return null;
  }, [maxHours, maxHoursNum]);

  const percentError =
    maxPercent && (!Number.isFinite(maxPercentNum) || maxPercentNum <= 0 || maxPercentNum > 100)
      ? 'Must be between 0 and 100.'
      : null;

  const isComplete = activeSelectionCount > 0 && (maxHours !== '' || maxPercent !== '');
  const canSubmit = isComplete && !hoursError && !percentError;
  const bothCapsSet = maxHours !== '' && maxPercent !== '';

  async function handleSubmit() {
    if (!canSubmit) return;
    setSaving(true);
    setSubmitError(null);
    try {
      // Only the active basis submits; the inactive one is cleared so an edit
      // that switches basis replaces both arrays atomically.
      await onSubmit({
        name: name.trim() || null,
        deliveryMethods: basis === 'delivery' ? methods : [],
        educationCategoryIds: basis === 'field' ? categories.map((c) => c.id) : null,
        maxHours: maxHours ? maxHoursNum : null,
        maxPercent: maxPercent ? maxPercentNum : null,
        scope,
      });
      onClose();
    } catch (error) {
      setSubmitError(
        error instanceof Error ? error.message : 'Something went wrong. Please try again.'
      );
    } finally {
      setSaving(false);
    }
  }

  const categoriesLoading = basis === 'field' && categoryOptions === null && !categoryLoadFailed;

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth fullScreen={isMobile}>
      <DialogTitle>{limit ? 'Edit credit limit' : 'Add credit limit'}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <Box>
            <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 0.75 }}>
              Limit by
            </Typography>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={basis}
              onChange={(_, value: CreditLimitBasis | null) => {
                // Both selections stay alive while toggling; only the active
                // basis submits.
                if (value) setBasis(value);
              }}
            >
              <ToggleButton value="delivery">Delivery methods</ToggleButton>
              <ToggleButton value="field">Fields of study</ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {basis === 'delivery' ? (
            <Autocomplete
              multiple
              options={[...DELIVERY_METHOD_TOKENS]}
              getOptionLabel={getDeliveryMethodLabel}
              value={methods}
              onChange={(_, value) => setMethods(value)}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={option}
                    label={getDeliveryMethodLabel(option)}
                    size="small"
                    variant="outlined"
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Delivery methods"
                  placeholder="e.g. Self-Study"
                  size="small"
                  helperText={
                    methods.length > 1
                      ? 'These methods share one cap; their hours are combined against it. For separate caps, add a limit per method.'
                      : undefined
                  }
                />
              )}
            />
          ) : (
            <Autocomplete
              multiple
              options={categoryOptions ?? []}
              loading={categoriesLoading}
              getOptionLabel={(option) => option.name}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              value={categories}
              onChange={(_, value) => setCategories(value)}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={option.id}
                    label={option.name}
                    size="small"
                    variant="outlined"
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Fields of study"
                  placeholder="e.g. Taxes"
                  size="small"
                  error={categoryLoadFailed}
                  helperText={
                    categoryLoadFailed
                      ? 'Couldn\u2019t load fields of study. Close and reopen the dialog to retry.'
                      : categories.length > 1
                        ? 'These fields share one cap; their hours are combined against it. For separate caps, add a limit per field.'
                        : undefined
                  }
                  slotProps={{
                    input: {
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {categoriesLoading ? <CircularProgress size={16} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    },
                  }}
                />
              )}
            />
          )}

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: 1.5 }}>
            <TextField
              label="Max hours"
              type="number"
              size="small"
              value={maxHours}
              onChange={(e) => setMaxHours(e.target.value)}
              error={!!hoursError}
              helperText={hoursError}
              slotProps={{ htmlInput: { min: 0.1, step: 0.1 } }}
            />
            <TextField
              label="Max percent of total"
              type="number"
              size="small"
              value={maxPercent}
              onChange={(e) => setMaxPercent(e.target.value)}
              error={!!percentError}
              helperText={percentError}
              slotProps={{ htmlInput: { min: 1, max: 100, step: 1 } }}
            />
            <TextField
              select
              label="Applies to"
              size="small"
              value={scope}
              onChange={(e) => setScope(e.target.value as SubjectRequirementScope)}
            >
              <MenuItem value="total_period">Entire reporting period</MenuItem>
              <MenuItem value="per_year">Each year of the period</MenuItem>
            </TextField>
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ mt: -1 }}>
            Hours over the limit stop counting toward your total.
            {bothCapsSet && ' The stricter of the two caps applies.'}
          </Typography>

          <TextField
            label="Name (optional)"
            size="small"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={
              suggestedName ? undefined : basis === 'delivery' ? 'e.g. Self-Study limit' : 'e.g. Taxes limit'
            }
            helperText={
              suggestedName && !name.trim() ? `Saved as "${suggestedName}"` : undefined
            }
          />

          {submitError && <Alert severity="error">{submitError}</Alert>}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={saving || !canSubmit}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {limit ? 'Save changes' : 'Add limit'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
